import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { InlineSpinner } from "@/components/common/Loading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ProgramPageView, PROGRAM_ICONS } from "@/pages/programs/ProgramPage";
import { GraduationCap, ArrowLeft, ArrowRight } from "lucide-react";

export default function PortalPrograms({ onGoToTeam, active, setActive }) {
  const [list, setList] = useState(null);
  const [page, setPage] = useState(null);

  useEffect(() => {
    api.get("/portal/programs").then((r) => setList(r.data)).catch(() => setList({ items: [] }));
  }, []);

  useEffect(() => {
    if (!active) { setPage(null); return; }
    setPage(null);
    api.get(`/portal/programs/${active}`).then((r) => setPage(r.data)).catch(() => setActive(null));
  }, [active]);

  if (list === null) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;

  if (active) {
    return (
      <div data-testid="portal-program-detail">
        <Button variant="ghost" size="sm" onClick={() => setActive(null)} className="mb-4" data-testid="portal-program-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All Programs
        </Button>
        {page === null ? <div className="py-16 flex justify-center"><InlineSpinner /></div> : <ProgramPageView pg={page} isMine={page.is_mine} onNavigate={(s) => setActive(s)} onCta={(action) => {
          if (["message_recruiter", "talk_recruiter", "contact_recruiter"].includes(action)) {
            if (onGoToTeam) onGoToTeam(); else window.dispatchEvent(new Event("tr-open-support"));
          }
        }} />}
      </div>
    );
  }

  const items = list.items || [];
  return (
    <div data-testid="portal-programs">
      <h1 className="font-display text-2xl font-bold text-slate-900">Farmers Programs</h1>
      <p className="mt-1 text-sm text-slate-600 mb-6">Explore the paths to agency ownership. Your current program is highlighted.</p>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No program information is available for your account yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((p) => {
            const Icon = PROGRAM_ICONS[p.icon] || GraduationCap;
            return (
              <button key={p.slug} onClick={() => setActive(p.slug)} data-testid={`portal-program-card-${p.slug}`}
                className={`group text-left rounded-xl border bg-white p-5 shadow-sm hover:shadow-md transition-all ${p.is_mine ? "border-[#0D9488] ring-1 ring-[#0D9488]/30" : "border-slate-200 hover:border-[#006090]"}`}>
                <div className="flex items-start gap-3">
                  <span className="rounded-lg bg-[#006090]/10 text-[#006090] p-2 shrink-0"><Icon className="w-5 h-5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900">{p.title}</p>
                      {p.is_mine && <Badge className="bg-[#0D9488] text-white border-0 text-[10px]" data-testid={`portal-your-program-${p.slug}`}>Your Program</Badge>}
                    </div>
                    {p.overview && <p className="text-xs text-slate-500 mt-1 line-clamp-3">{p.overview}</p>}
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#006090]">Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
